import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, Clock, Copy, Download, FileText, Mail, MapPin, MessageCircle, PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen, Paperclip, Phone, Plus, RefreshCw, Save, Send, Trash2, User as UserIcon, Users, X } from 'lucide-react';
import { User } from '../../../models/auth';
import { callWhatsappChatApi } from './api';
import pdfIcon from '../../../assets/images/pdf-icon.png';

// Textos que pone el backend cuando un mensaje no tiene texto: solo adjuntos o solo una ubicación
const CUERPO_SOLO_ADJUNTO = '[Archivo adjunto]';
const CUERPO_SOLO_UBICACION = '[Ubicación]';
// Límite de subir_documento_chat en el backend (solo admite PDF)
const DOCUMENTO_MAX_BYTES = 16 * 1024 * 1024;

const esImagen = (adjunto: WhatsappAdjunto) => adjunto.tipo.startsWith('image/');

// Mensaje sin texto cuyos adjuntos son todos PDF: se muestra solo el icono (que descarga el PDF)
const esSoloPdf = (msg: WhatsappMessage): msg is WhatsappMessage & { adjuntos: WhatsappAdjunto[] } =>
  msg.cuerpo === CUERPO_SOLO_ADJUNTO && !!msg.adjuntos?.length && msg.adjuntos.every((a) => a.tipo === 'application/pdf');

const urlMapa = (ubicacion: WhatsappUbicacion) =>
  `https://www.google.com/maps/search/?api=1&query=${ubicacion.latitud},${ubicacion.longitud}`;
const urlMapaEmbebido = (ubicacion: WhatsappUbicacion) =>
  `https://maps.google.com/maps?q=${ubicacion.latitud},${ubicacion.longitud}&z=15&output=embed`;

// Panel compartido de atención humana por WhatsApp para cualquier servicio.
// Uso: <WhatsappChatPanel user apiUrl apiKey prefijo="impagos_chat_" titulo="..." />
// El prefijo elige el módulo del backend que atiende las acciones (p. ej. atencion_practicas_ o impagos_chat_).
// Los estilos ap-* están en ServiceManagement/css/serviceManagement.css (se cargan con ServiceManagementApp).

const MESSAGES_REFRESH_MS = 10 * 1000; // los mensajes se piden a Twilio cada 10 segundos
// Con la pestaña oculta se sigue preguntando (más despacio) para poder avisar de los mensajes nuevos
const HIDDEN_MESSAGES_REFRESH_MS = 30 * 1000;
const TITLE_BLINK_MS = 1000;
const BADGE_COLOR = '#E02424';
// Paneles redimensionables (lista de conversaciones y ficha del alumno)
const ANCHOS_POR_DEFECTO = { lista: 200, ficha: 200 };
const PANEL_MIN_PX = 200;
const PANEL_MAX_PX = 560;
const CHAT_MIN_PX = 320;
const PANEL_PLEGADO_PX = 44;

// Favicon de la pestaña con un círculo rojo y el número de mensajes sin leer (como Teams o WhatsApp Web)
const faviconConAviso = (base: HTMLImageElement | null, count: number) => {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  if (base?.complete && base.naturalWidth) ctx.drawImage(base, 0, 0, size, size);
  const radius = size * 0.3;
  const cx = size - radius;
  const cy = radius;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
  ctx.fillStyle = BADGE_COLOR;
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = '#FFFFFF';
  ctx.stroke();
  ctx.fillStyle = '#FFFFFF';
  ctx.font = `bold ${count > 9 ? 22 : 28}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(count > 99 ? '99+' : String(count), cx, cy + 1);
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null; // si el favicon viniera de otro dominio el canvas queda bloqueado: se deja el original
  }
};
const PRESENCE_REFRESH_MS = 10 * 1000; // latido de presencia (quién está en cada conversación)
const TYPING_TIMEOUT_MS = 6 * 1000;
// WhatsApp solo deja enviar texto libre en las 24 h siguientes al último mensaje del usuario:
// solo se muestran las conversaciones a las que todavía se puede contestar
const REPLY_WINDOW_MS = 24 * 60 * 60 * 1000;
const CLOCK_TICK_MS = 60 * 1000;

const DAYS: { key: string; label: string }[] = [
  { key: 'lunes', label: 'Lunes' },
  { key: 'martes', label: 'Martes' },
  { key: 'miercoles', label: 'Miércoles' },
  { key: 'jueves', label: 'Jueves' },
  { key: 'viernes', label: 'Viernes' },
  { key: 'sabado', label: 'Sábado' },
  { key: 'domingo', label: 'Domingo' },
];

interface Franja {
  inicio: string;
  fin: string;
}

interface AtencionConfig {
  activo: boolean;
  zona_horaria: string;
  franjas: Record<string, Franja[]>;
  mensaje_recepcion: string;
  actualizado_por?: string;
  actualizado?: string;
}

interface WhatsappAdjunto {
  sid: string;
  tipo: string;
  nombre: string;
}

// Ubicación compartida por el usuario (el backend la guarda al recibir el webhook de Twilio)
interface WhatsappUbicacion {
  latitud: number;
  longitud: number;
  direccion?: string;
  etiqueta?: string;
}

interface WhatsappMessage {
  sid: string;
  entrante: boolean;
  cuerpo: string;
  fecha: string | null;
  estado?: string;
  autor?: string | null;
  humano?: boolean;
  adjuntos?: WhatsappAdjunto[];
  ubicacion?: WhatsappUbicacion | null;
}

interface WhatsappConversation {
  telefono: string;
  nombre: string;
  correo: string;
  mensajes: WhatsappMessage[];
  ultimo_mensaje: string | null;
  ultimo_mensaje_usuario: string | null;
  puede_responder: boolean;
  // Persona responsable que tiene la conversación tomada: el asistente no contesta mientras la tenga
  control?: { email: string; nombre: string } | null;
}

interface PresenceUser {
  email: string;
  nombre: string;
  escribiendo: boolean;
}

// Hasta qué mensaje del usuario se ha leído una conversación y quién lo leyó (compartido entre agentes)
interface ReadInfo {
  hasta: string;
  email: string;
  nombre: string;
}

const toMs = (iso?: string | null) => (iso ? Date.parse(iso) || 0 : 0);

// Une el estado de leídos quedándose con la lectura más reciente de cada conversación,
// para que un latido que llega tarde no vuelva a marcar como no leído lo que se acaba de leer
const mergeReads = (prev: Record<string, ReadInfo>, incoming?: Record<string, ReadInfo> | null) => {
  if (!incoming) return prev;
  const next = { ...prev };
  Object.entries(incoming).forEach(([phone, info]) => {
    if (!next[phone] || toMs(info.hasta) >= toMs(next[phone].hasta)) next[phone] = info;
  });
  return next;
};

export interface WhatsappChatPanelProps {
  user: User;
  apiUrl: string;
  apiKey: string;
  // Prefijo de las acciones del backend, p. ej. 'atencion_practicas_' o 'impagos_chat_'
  prefijo: string;
  titulo: string;
  // Muestra el botón «Horario de atención» (solo si el backend del servicio lo soporta: acciones config/guardar_config)
  conHorario?: boolean;
  // Botones extra de la cabecera (se colocan antes de «Actualizar»)
  accionesCabecera?: React.ReactNode;
  // Contenido extra debajo de la cabecera (p. ej. un editor de alumnos)
  bajoCabecera?: React.ReactNode;
}

const formatTime = (iso: string | null) => {
  if (!iso) return '';
  const date = new Date(iso);
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const initials = (name: string) =>
  name
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

// Color estable por persona, como los cursores de Excel online
const AVATAR_COLORS = ['#003CA3', '#52A095', '#B4462B', '#7A4FB0', '#C4861A', '#188FF1', '#A23B72'];
const avatarColor = (email: string) => {
  let hash = 0;
  for (let i = 0; i < email.length; i += 1) hash = (hash * 31 + email.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const Avatar: React.FC<{ person: PresenceUser; size?: number }> = ({ person, size = 26 }) => (
  <span
    className="ap-avatar"
    style={{ background: avatarColor(person.email), width: size, height: size, fontSize: size * 0.42 }}
    title={`${person.nombre}${person.escribiendo ? ' (escribiendo…)' : ''}`}
  >
    {initials(person.nombre)}
  </span>
);

const copiarTexto = async (texto: string) => {
  try {
    await navigator.clipboard.writeText(texto);
  } catch {
    // Sin permiso de portapapeles (o fuera de https): copia clásica con un textarea temporal
    const area = document.createElement('textarea');
    area.value = texto;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    document.execCommand('copy');
    document.body.removeChild(area);
  }
};

// Ficha con los datos del usuario de la conversación abierta; cada dato se copia con un clic
const FichaUsuario: React.FC<{ conversation: WhatsappConversation; plegada: boolean; onToggle: () => void }> = ({
  conversation,
  plegada,
  onToggle,
}) => {
  const [copiado, setCopiado] = useState<string | null>(null);
  const timeoutRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

  const datos = [
    { clave: 'nombre', etiqueta: 'Nombre', valor: conversation.nombre, icono: <UserIcon size={14} /> },
    { clave: 'correo', etiqueta: 'Correo', valor: conversation.correo, icono: <Mail size={14} /> },
    { clave: 'telefono', etiqueta: 'Teléfono', valor: conversation.telefono ? `+${conversation.telefono}` : '', icono: <Phone size={14} /> },
  ].filter((dato) => dato.valor);

  const copiar = async (clave: string, texto: string) => {
    await copiarTexto(texto);
    setCopiado(clave);
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => setCopiado(null), 1500);
  };

  if (plegada) {
    return (
      <aside className="ap-ficha is-collapsed">
        <button type="button" className="ap-collapse-btn" onClick={onToggle} title="Mostrar datos del alumno" aria-label="Mostrar datos del alumno">
          <PanelRightOpen size={18} />
        </button>
        <span className="ap-collapsed-label">Datos Alumno</span>
      </aside>
    );
  }

  return (
    <aside className="ap-ficha">
      <div className="ap-panel-head">
        <h3 className="ap-ficha-titulo">
          <UserIcon size={16} /> Datos Alumno
        </h3>
        <button type="button" className="ap-collapse-btn" onClick={onToggle} title="Plegar datos del alumno" aria-label="Plegar datos del alumno">
          <PanelRightClose size={18} />
        </button>
      </div>
      {!datos.length && <p className="ap-muted">Sin datos del alumno.</p>}
      {datos.map((dato) => (
        <button
          key={dato.clave}
          type="button"
          className={`ap-ficha-dato ${copiado === dato.clave ? 'is-copied' : ''}`}
          onClick={() => copiar(dato.clave, dato.valor)}
          title={`Copiar ${dato.etiqueta.toLowerCase()}`}
        >
          <span className="ap-ficha-icono">{dato.icono}</span>
          <span className="ap-ficha-texto">
            <span className="ap-ficha-etiqueta">{dato.etiqueta}</span>
            <span className="ap-ficha-valor">{dato.valor}</span>
          </span>
          {copiado === dato.clave ? <Check size={14} /> : <Copy size={14} />}
        </button>
      ))}
      {datos.length > 1 && (
        <button
          type="button"
          className={`ap-btn ap-btn-secondary ap-ficha-todo ${copiado === 'todo' ? 'is-copied' : ''}`}
          onClick={() => copiar('todo', datos.map((dato) => `${dato.etiqueta}: ${dato.valor}`).join('\n'))}
        >
          {copiado === 'todo' ? <Check size={16} /> : <Copy size={16} />} {copiado === 'todo' ? 'Copiado' : 'Copiar todo'}
        </button>
      )}
    </aside>
  );
};

// Lee un fichero en base64 (sin el prefijo data:...;base64,)
const leerComoBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',', 2)[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const WhatsappChatPanel: React.FC<WhatsappChatPanelProps> = ({
  user,
  apiUrl,
  apiKey,
  prefijo,
  titulo,
  conHorario = false,
  accionesCabecera,
  bajoCabecera,
}) => {
  const userEmail = user.email.toLowerCase().trim();
  const userName = user.name || user.email;

  const [conversations, setConversations] = useState<WhatsappConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [presence, setPresence] = useState<Record<string, PresenceUser[]>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // PDF elegido para enviar en la conversación abierta (se envía con el botón Enviar)
  const [documentoPendiente, setDocumentoPendiente] = useState<File | null>(null);
  const documentoInputRef = useRef<HTMLInputElement | null>(null);
  const [arrastrando, setArrastrando] = useState(false);
  // Imágenes recibidas ya descargadas de Twilio: sid del adjunto -> URL local (o null mientras se descarga)
  const [imagenes, setImagenes] = useState<Record<string, string | null>>({});
  const imagenesRef = useRef(imagenes);
  imagenesRef.current = imagenes;
  const [search, setSearch] = useState('');
  // Lista de conversaciones y ficha del alumno plegables para dar más ancho al chat (se recuerda en este navegador)
  const panelesKey = `${prefijo}paneles_plegados`;
  const [plegados, setPlegados] = useState<{ lista: boolean; ficha: boolean }>(() => {
    try {
      return { lista: false, ficha: false, ...JSON.parse(localStorage.getItem(panelesKey) || '{}') };
    } catch {
      return { lista: false, ficha: false };
    }
  });
  const listaPlegada = plegados.lista;
  const fichaPlegada = plegados.ficha;
  const togglePanel = (panel: 'lista' | 'ficha') =>
    setPlegados((prev) => {
      const next = { ...prev, [panel]: !prev[panel] };
      try {
        localStorage.setItem(panelesKey, JSON.stringify(next));
      } catch {
        // Sin almacenamiento local simplemente no se recuerda
      }
      return next;
    });

  // Ancho de la lista y de la ficha: se cambia arrastrando el tirador que hay entre paneles (doble clic: ancho por defecto)
  const anchosKey = `${prefijo}paneles_anchos`;
  const [anchos, setAnchos] = useState<{ lista: number; ficha: number }>(() => {
    try {
      return { ...ANCHOS_POR_DEFECTO, ...JSON.parse(localStorage.getItem(anchosKey) || '{}') };
    } catch {
      return ANCHOS_POR_DEFECTO;
    }
  });
  const anchosRef = useRef(anchos);
  anchosRef.current = anchos;
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const [redimensionando, setRedimensionando] = useState<'lista' | 'ficha' | null>(null);

  const guardarAnchos = (next: { lista: number; ficha: number }) => {
    try {
      localStorage.setItem(anchosKey, JSON.stringify(next));
    } catch {
      // Sin almacenamiento local simplemente no se recuerda
    }
  };

  const empezarRedimension = (panel: 'lista' | 'ficha', e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const inicioX = e.clientX;
    const inicio = anchosRef.current[panel];
    const anchoLayout = layoutRef.current?.clientWidth || window.innerWidth;
    const fichaVisible = !!selectedPhoneRef.current;
    const otro =
      panel === 'lista'
        ? fichaVisible ? (plegados.ficha ? PANEL_PLEGADO_PX : anchosRef.current.ficha) : 0
        : plegados.lista ? PANEL_PLEGADO_PX : anchosRef.current.lista;
    // Lo que quede tiene que dejar sitio al chat (y a los dos huecos de 12 px entre columnas)
    const maximo = Math.max(PANEL_MIN_PX, Math.min(PANEL_MAX_PX, anchoLayout - otro - CHAT_MIN_PX - 24));
    setRedimensionando(panel);

    const mover = (ev: PointerEvent) => {
      const delta = panel === 'lista' ? ev.clientX - inicioX : inicioX - ev.clientX;
      const ancho = Math.round(Math.min(maximo, Math.max(PANEL_MIN_PX, inicio + delta)));
      setAnchos((prev) => ({ ...prev, [panel]: ancho }));
    };
    const soltar = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      setRedimensionando(null);
      guardarAnchos(anchosRef.current);
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
  };

  const restablecerAncho = (panel: 'lista' | 'ficha') => {
    const next = { ...anchosRef.current, [panel]: ANCHOS_POR_DEFECTO[panel] };
    setAnchos(next);
    guardarAnchos(next);
  };
  const [reads, setReads] = useState<Record<string, ReadInfo>>({});
  // Por conversación abierta: lo leído antes de abrirla; los mensajes posteriores se destacan como nuevos
  const [highlightFrom, setHighlightFrom] = useState<Record<string, number>>({});
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState === 'visible');
  const [now, setNow] = useState(() => Date.now());

  const [config, setConfig] = useState<AtencionConfig | null>(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configFeedback, setConfigFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const lastTypingRef = useRef(0);
  const selectedPhoneRef = useRef<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  selectedPhoneRef.current = selectedPhone;

  // accion sin prefijo: 'conversaciones' se envía como <prefijo>conversaciones (p. ej. impagos_chat_conversaciones)
  const callApi = useCallback(
    (accion: string, body: Record<string, unknown> = {}) => callWhatsappChatApi(apiUrl, apiKey, `${prefijo}${accion}`, body),
    [apiUrl, apiKey, prefijo]
  );

  // ---- Avisos de mensajes nuevos ----
  // Sids de los mensajes entrantes ya vistos; null hasta la primera carga (lo que ya había no se avisa)
  const seenIncomingRef = useRef<Set<string> | null>(null);
  // Conversaciones (teléfonos) en las que han entrado mensajes con la pestaña oculta, de la más reciente a la más antigua.
  // El aviso del título solo se muestra para las que siguen sin leer
  const [alertPhones, setAlertPhones] = useState<string[]>([]);

  const detectNewMessages = useCallback((convs: WhatsappConversation[]) => {
    const incoming = convs.flatMap((conv) => conv.mensajes.filter((msg) => msg.entrante).map((msg) => ({ conv, msg })));
    const seen = seenIncomingRef.current;
    seenIncomingRef.current = new Set(incoming.map(({ msg }) => msg.sid));
    if (!seen || document.visibilityState === 'visible') return;
    const phones = Array.from(new Set(incoming.filter(({ msg }) => !seen.has(msg.sid)).map(({ conv }) => conv.telefono))).reverse();
    if (!phones.length) return;
    setAlertPhones((prev) => [...phones, ...prev.filter((phone) => !phones.includes(phone))]);
  }, []);

  const loadingRef = useRef(false);
  // showSpinner solo al pulsar «Actualizar»: el refresco automático es silencioso para no parpadear cada 10 s
  const loadConversations = useCallback(async (showSpinner = false) => {
    if (loadingRef.current) return; // si Twilio tarda más de 10 s no se amontonan peticiones
    loadingRef.current = true;
    if (showSpinner) setLoading(true);
    try {
      const data = await callApi('conversaciones', { horas: 24 });
      detectNewMessages(data.conversaciones || []);
      setConversations(data.conversaciones || []);
      setReads((prev) => mergeReads(prev, data.leidos));
      setConfig((prev) => prev ?? data.config);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'No se pudieron cargar las conversaciones.');
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [callApi, detectNewMessages]);

  const sendHeartbeat = useCallback(async () => {
    const phone = selectedPhoneRef.current;
    const typing = Date.now() - lastTypingRef.current < TYPING_TIMEOUT_MS;
    try {
      const data = await callApi('presencia', {
        email: userEmail,
        nombre: userName,
        telefono: phone || '',
        escribiendo: typing,
      });
      setPresence(data.presencia || {});
      setReads((prev) => mergeReads(prev, data.leidos));
    } catch {
      // La presencia es informativa: si falla un latido se reintenta en el siguiente
    }
  }, [callApi, userEmail, userName]);

  // Mensajes: al entrar y cada 10 segundos con la pestaña visible (al volver a ella, enseguida);
  // con la pestaña oculta cada 30 segundos, para poder avisar de los mensajes nuevos
  useEffect(() => {
    if (pageVisible) loadConversations(true);
    const id = window.setInterval(() => loadConversations(), pageVisible ? MESSAGES_REFRESH_MS : HIDDEN_MESSAGES_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [loadConversations, pageVisible]);

  // Presencia: latido periódico y al cambiar de conversación; al salir se avisa al backend
  useEffect(() => {
    sendHeartbeat();
    const id = window.setInterval(sendHeartbeat, PRESENCE_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [sendHeartbeat, selectedPhone]);

  useEffect(() => {
    const leave = () => {
      if (!apiUrl || !apiKey) return;
      const blob = new Blob([JSON.stringify({ email: userEmail })], { type: 'application/json' });
      navigator.sendBeacon(`${apiUrl}?code=${apiKey}&accion=${prefijo}salir`, blob);
    };
    window.addEventListener('beforeunload', leave);
    return () => {
      window.removeEventListener('beforeunload', leave);
      leave();
    };
  }, [apiUrl, apiKey, userEmail, prefijo]);

  const selectedConversation = useMemo(
    () => conversations.find((conv) => conv.telefono === selectedPhone) || null,
    [conversations, selectedPhone]
  );

  useEffect(() => {
    const onVisibility = () => setPageVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const unreadCount = useCallback(
    (conv: WhatsappConversation) => {
      const readUntil = toMs(reads[conv.telefono]?.hasta);
      return conv.mensajes.filter((msg) => msg.entrante && toMs(msg.fecha) > readUntil).length;
    },
    [reads]
  );

  // La conversación abierta (con la pestaña visible) se marca como leída para todos
  useEffect(() => {
    if (!selectedConversation || !pageVisible) return;
    const phone = selectedConversation.telefono;
    const lastIncoming = [...selectedConversation.mensajes].reverse().find((msg) => msg.entrante)?.fecha;
    const readUntil = reads[phone]?.hasta;
    if (!lastIncoming || toMs(lastIncoming) <= toMs(readUntil)) return;

    setHighlightFrom((prev) => (phone in prev ? prev : { ...prev, [phone]: toMs(readUntil) }));
    setReads((prev) => mergeReads(prev, { [phone]: { hasta: lastIncoming, email: userEmail, nombre: userName } }));
    callApi('marcar_leido', { telefono: phone, hasta: lastIncoming, email: userEmail, nombre: userName })
      .then((data) => setReads((prev) => mergeReads(prev, data.leidos)))
      .catch(() => {
        // Si falla se reintenta cuando cambien los mensajes o el estado de leídos
      });
  }, [selectedConversation, reads, pageVisible, callApi, userEmail, userName]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ block: 'end' });
  }, [selectedConversation?.mensajes.length, selectedPhone]);

  const othersIn = useCallback(
    (phone: string) => (presence[phone] || []).filter((person) => person.email !== userEmail),
    [presence, userEmail]
  );

  const connectedUsers = useMemo(() => {
    const byEmail = new Map<string, PresenceUser>();
    Object.values(presence).forEach((people) => people.forEach((person) => byEmail.set(person.email, person)));
    return Array.from(byEmail.values());
  }, [presence]);

  // Cada minuto se quitan de la lista las conversaciones que salen de la ventana de 24 h
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const canReply = useCallback(
    (conv: WhatsappConversation) => now - toMs(conv.ultimo_mensaje_usuario) < REPLY_WINDOW_MS,
    [now]
  );

  // Pestaña: «(n) Nuevos mensajes · título» y favicon con el número de sin leer;
  // si está oculta y llega algo, el título alterna con «Nombre te ha escrito» hasta que se vuelve a ella
  const originalTitleRef = useRef(document.title);
  const faviconLinkRef = useRef<HTMLLinkElement | null>(document.querySelector<HTMLLinkElement>('link[rel~="icon"]'));
  const originalFaviconRef = useRef(faviconLinkRef.current?.href || '');
  const faviconImgRef = useRef<HTMLImageElement | null>(null);
  const [faviconReady, setFaviconReady] = useState(false);

  useEffect(() => {
    if (!originalFaviconRef.current) return;
    const img = new Image();
    img.onload = () => setFaviconReady(true);
    img.src = originalFaviconRef.current;
    faviconImgRef.current = img;
  }, []);
  const totalUnread = useMemo(
    () => conversations.filter(canReply).reduce((total, conv) => total + unreadCount(conv), 0),
    [conversations, canReply, unreadCount]
  );

  useEffect(() => {
    if (pageVisible) setAlertPhones([]);
  }, [pageVisible]);

  // Se recalcula con cada cambio de leídos: si alguien lee la conversación, su aviso desaparece
  const tabAlert = useMemo(() => {
    const pendientes = alertPhones
      .map((phone) => conversations.find((conv) => conv.telefono === phone))
      .filter((conv): conv is WhatsappConversation => !!conv && canReply(conv) && unreadCount(conv) > 0);
    if (!pendientes.length) return null;
    if (pendientes.length > 1) return `${pendientes.length} conversaciones con mensajes nuevos`;
    const conv = pendientes[0];
    return `${conv.nombre || conv.correo || `+${conv.telefono}`} te ha escrito`;
  }, [alertPhones, conversations, canReply, unreadCount]);

  useEffect(() => {
    const link = faviconLinkRef.current;
    if (!link) return;
    link.href = (totalUnread > 0 && faviconConAviso(faviconImgRef.current, totalUnread)) || originalFaviconRef.current;
  }, [totalUnread, faviconReady]);

  useEffect(() => {
    const base =
      totalUnread > 0
        ? `(${totalUnread}) ${totalUnread === 1 ? 'Nuevo mensaje' : 'Nuevos mensajes'} · ${originalTitleRef.current}`
        : originalTitleRef.current;
    document.title = base;
    if (!tabAlert) return;
    let showAlert = false;
    const id = window.setInterval(() => {
      showAlert = !showAlert;
      document.title = showAlert ? tabAlert : base;
    }, TITLE_BLINK_MS);
    return () => {
      window.clearInterval(id);
      document.title = base;
    };
  }, [totalUnread, tabAlert]);

  useEffect(
    () => () => {
      document.title = originalTitleRef.current;
      if (faviconLinkRef.current) faviconLinkRef.current.href = originalFaviconRef.current;
    },
    []
  );

  const filteredConversations = useMemo(() => {
    const term = search.trim().toLowerCase();
    return conversations.filter(
      (conv) =>
        canReply(conv) &&
        (!term ||
          conv.telefono.includes(term) ||
          conv.nombre.toLowerCase().includes(term) ||
          conv.correo.toLowerCase().includes(term))
    );
  }, [conversations, search, canReply]);

  const handleSelect = (phone: string) => {
    // Lo que ya hubiera leído alguien (yo u otra persona) no se destaca al abrirla
    setHighlightFrom((prev) => ({ ...prev, [phone]: toMs(reads[phone]?.hasta) }));
    setSelectedPhone(phone);
    setSendError(null);
    quitarDocumento();
  };

  const quitarDocumento = () => {
    setDocumentoPendiente(null);
    if (documentoInputRef.current) documentoInputRef.current.value = '';
  };

  const elegirDocumento = (file: File | null | undefined) => {
    if (!file) return;
    const esPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!esPdf) {
      setSendError(`Solo se pueden enviar ficheros PDF (${file.name}).`);
      return;
    }
    if (file.size > DOCUMENTO_MAX_BYTES) {
      setSendError(`El PDF ${file.name} supera el tamaño máximo de 16 MB.`);
      return;
    }
    setSendError(null);
    setDocumentoPendiente(file);
  };

  // Soltar un PDF en el chat lo deja listo para enviar, igual que elegirlo con el botón de adjuntar
  const puedeSoltar = !!selectedConversation && canReply(selectedConversation) && !sending;
  const esArrastreDeArchivos = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes('Files');

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!puedeSoltar || !esArrastreDeArchivos(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!arrastrando) setArrastrando(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setArrastrando(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (!esArrastreDeArchivos(e)) return;
    e.preventDefault();
    setArrastrando(false);
    if (!puedeSoltar) return;
    // Un PDF por mensaje: si se sueltan varios se coge el primero
    elegirDocumento(e.dataTransfer.files[0]);
  };

  const handleDraftChange = (value: string) => {
    if (!selectedPhone) return;
    setDrafts((prev) => ({ ...prev, [selectedPhone]: value }));
    const wasTyping = Date.now() - lastTypingRef.current < TYPING_TIMEOUT_MS;
    lastTypingRef.current = Date.now();
    if (!wasTyping) sendHeartbeat(); // avisa enseguida a los demás de que se está escribiendo
  };

  const handleSend = async () => {
    if (!selectedConversation || sending) return;
    const phone = selectedConversation.telefono;
    const message = (drafts[phone] || '').trim();
    const documento = documentoPendiente;
    if (!message && !documento) return;

    setSending(true);
    setSendError(null);
    try {
      const cuerpo: Record<string, unknown> = {
        telefono: phone,
        correo: selectedConversation.correo, // si el teléfono no llega, el backend lo busca por correo
        mensaje: message,
        email: userEmail,
        nombre: userName,
      };
      if (documento) {
        cuerpo.documento = { nombre: documento.name, contenido: await leerComoBase64(documento) };
      }
      const data = await callApi('enviar', cuerpo);
      const textoMostrado = documento ? `Documento: ${documento.name}${message ? ` · ${message}` : ''}` : message;
      // Se añade ya a la conversación; el siguiente refresco lo trae de Twilio con su estado real
      setConversations((prev) =>
        prev.map((conv) =>
          conv.telefono === phone
            ? {
                ...conv,
                ultimo_mensaje: data.fecha,
                mensajes: [
                  ...conv.mensajes,
                  { sid: data.sid, entrante: false, cuerpo: textoMostrado, fecha: data.fecha, estado: 'queued', autor: userName, humano: true },
                ],
              }
            : conv
        )
      );
      setDrafts((prev) => ({ ...prev, [phone]: '' }));
      quitarDocumento();
      lastTypingRef.current = 0;
      sendHeartbeat();
    } catch (err: any) {
      setSendError(err.message || 'No se pudo enviar el mensaje.');
    } finally {
      setSending(false);
    }
  };

  // ---- Adjuntos ----
  // «Abrir» muestra el fichero en una pestaña nueva y «Descargar» lo guarda; los dos lo piden al backend
  const abrirAdjunto = async (mensajeSid: string, adjunto: WhatsappAdjunto, modo: 'abrir' | 'descargar') => {
    // La pestaña se abre antes de pedir el fichero para que el navegador no la bloquee como ventana emergente
    const ventana = modo === 'abrir' ? window.open('', '_blank') : null;
    setSendError(null);
    try {
      const data = await callApi('descargar_adjunto', { mensaje_sid: mensajeSid, media_sid: adjunto.sid });
      const bytes = Uint8Array.from(atob(data.contenido), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: data.tipo }));
      if (ventana) {
        ventana.location.href = url;
      } else {
        const enlace = document.createElement('a');
        enlace.href = url;
        enlace.download = adjunto.nombre || data.nombre;
        enlace.click();
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    } catch (err: any) {
      ventana?.close();
      setSendError(err.message || 'No se pudo obtener el adjunto.');
    }
  };

  // Las imágenes que envía el usuario se descargan de Twilio (vía backend) para verlas en el chat
  useEffect(() => {
    if (!selectedConversation) return;
    selectedConversation.mensajes.forEach((msg) => {
      msg.adjuntos?.filter(esImagen).forEach((adjunto) => {
        if (adjunto.sid in imagenesRef.current) return;
        imagenesRef.current = { ...imagenesRef.current, [adjunto.sid]: null };
        setImagenes((prev) => ({ ...prev, [adjunto.sid]: null }));
        callApi('descargar_adjunto', { mensaje_sid: msg.sid, media_sid: adjunto.sid })
          .then((data) => {
            const bytes = Uint8Array.from(atob(data.contenido), (c) => c.charCodeAt(0));
            const url = URL.createObjectURL(new Blob([bytes], { type: data.tipo }));
            setImagenes((prev) => ({ ...prev, [adjunto.sid]: url }));
          })
          .catch(() => {
            // Se quita de la caché para reintentarlo en el siguiente refresco
            setImagenes((prev) => {
              const next = { ...prev };
              delete next[adjunto.sid];
              return next;
            });
          });
      });
    });
  }, [selectedConversation, callApi]);

  useEffect(
    () => () => {
      Object.values(imagenesRef.current).forEach((url) => url && URL.revokeObjectURL(url));
    },
    []
  );

  // ---- Control humano ----
  // Tomar la conversación hace que el asistente deje de contestar en ella hasta que se devuelva o se cierre la web
  const actualizarControl = (phone: string, control: WhatsappConversation['control']) =>
    setConversations((prev) => prev.map((conv) => (conv.telefono === phone ? { ...conv, control } : conv)));

  const tomarControl = async (phone: string) => {
    setSendError(null);
    try {
      const data = await callApi('tomar_control', { telefono: phone, email: userEmail, nombre: userName });
      actualizarControl(phone, data.control || { email: userEmail, nombre: userName });
    } catch (err: any) {
      setSendError(err.message || 'No se pudo tomar el control de la conversación.');
    }
  };

  const devolverControl = async (phone: string) => {
    setSendError(null);
    try {
      await callApi('devolver_control', { telefono: phone, email: userEmail });
      actualizarControl(phone, null);
    } catch (err: any) {
      setSendError(err.message || 'No se pudo devolver la conversación al asistente.');
    }
  };

  // ---- Horario ----
  const openSchedule = async () => {
    setShowSchedule((prev) => !prev);
    setConfigFeedback(null);
    if (!showSchedule) {
      try {
        const data = await callApi('config');
        setConfig(data.config);
      } catch (err: any) {
        setConfigFeedback({ type: 'error', text: err.message || 'No se pudo leer el horario.' });
      }
    }
  };

  const updateFranja = (day: string, index: number, field: keyof Franja, value: string) => {
    setConfig((prev) =>
      prev
        ? {
            ...prev,
            franjas: {
              ...prev.franjas,
              [day]: prev.franjas[day].map((franja, i) => (i === index ? { ...franja, [field]: value } : franja)),
            },
          }
        : prev
    );
  };

  const addFranja = (day: string) => {
    setConfig((prev) =>
      prev ? { ...prev, franjas: { ...prev.franjas, [day]: [...(prev.franjas[day] || []), { inicio: '09:00', fin: '14:00' }] } } : prev
    );
  };

  const removeFranja = (day: string, index: number) => {
    setConfig((prev) =>
      prev ? { ...prev, franjas: { ...prev.franjas, [day]: prev.franjas[day].filter((_, i) => i !== index) } } : prev
    );
  };

  const copyToWeekdays = (day: string) => {
    setConfig((prev) => {
      if (!prev) return prev;
      const franjas = { ...prev.franjas };
      ['lunes', 'martes', 'miercoles', 'jueves', 'viernes'].forEach((key) => {
        franjas[key] = prev.franjas[day].map((franja) => ({ ...franja }));
      });
      return { ...prev, franjas };
    });
  };

  const saveConfig = async () => {
    if (!config) return;
    setSavingConfig(true);
    setConfigFeedback(null);
    try {
      const data = await callApi('guardar_config', { config, usuario: userEmail });
      setConfig(data.config);
      setConfigFeedback({ type: 'success', text: 'Horario guardado.' });
    } catch (err: any) {
      setConfigFeedback({ type: 'error', text: err.message || 'No se pudo guardar el horario.' });
    } finally {
      setSavingConfig(false);
    }
  };


  const othersHere = selectedConversation ? othersIn(selectedConversation.telefono) : [];
  const othersTyping = othersHere.filter((person) => person.escribiendo);
  const firstNewSid = selectedConversation?.mensajes.find(
    (msg) => msg.entrante && toMs(msg.fecha) > (highlightFrom[selectedConversation.telefono] ?? Infinity)
  )?.sid;

  return (
    <section className="service-config-block ap-block">
      <div className="ap-header">
        <div>
          <h2>
            <MessageCircle size={20} /> {titulo}
          </h2>
          
        </div>
        <div className="ap-header-actions">
          <div className="ap-connected" title="Personas conectadas a este apartado">
            <Users size={16} />
            {connectedUsers.map((person) => (
              <Avatar key={person.email} person={person} />
            ))}
          </div>
          {conHorario && (
            <button type="button" className="ap-btn ap-btn-secondary" onClick={openSchedule}>
              <Clock size={16} /> Horario de atención
            </button>
          )}
          {accionesCabecera}
          <button type="button" className="ap-btn ap-btn-secondary" onClick={() => loadConversations(true)} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinner-icon' : ''} /> Actualizar
          </button>
        </div>
      </div>


      {showSchedule && config && (
        <div className="ap-schedule">
          

          <div className="ap-days">
            {DAYS.map((day) => (
              <div key={day.key} className="ap-day">
                <span className="ap-day-name">{day.label}</span>
                <div className="ap-franjas">
                  {(config.franjas[day.key] || []).length === 0 && <span className="ap-muted">Sin atención</span>}
                  {(config.franjas[day.key] || []).map((franja, index) => (
                    <span key={index} className="ap-franja">
                      <input type="time" value={franja.inicio} onChange={(e) => updateFranja(day.key, index, 'inicio', e.target.value)} />
                      –
                      <input type="time" value={franja.fin} onChange={(e) => updateFranja(day.key, index, 'fin', e.target.value)} />
                      <button type="button" className="ap-icon-btn" onClick={() => removeFranja(day.key, index)} title="Quitar franja">
                        <Trash2 size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="ap-day-actions">
                  <button type="button" className="ap-icon-btn" onClick={() => addFranja(day.key)} title="Añadir franja">
                    <Plus size={14} />
                  </button>
                  {day.key === 'lunes' && (
                    <button type="button" className="ap-link-btn" onClick={() => copyToWeekdays('lunes')}>
                      Copiar a lunes–viernes
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <label className="ap-field">
            Mensaje automático al recibir un mensaje dentro del horario (vacío = no se envía)
            <textarea
              rows={2}
              value={config.mensaje_recepcion}
              onChange={(e) => setConfig({ ...config, mensaje_recepcion: e.target.value })}
            />
          </label>

          <div className="ap-schedule-footer">
            <button type="button" className="ap-btn" onClick={saveConfig} disabled={savingConfig}>
              {savingConfig ? <RefreshCw size={16} className="spinner-icon" /> : <Save size={16} />} Guardar horario
            </button>
          </div>
          {configFeedback && <div className={`ap-feedback ap-feedback-${configFeedback.type}`}>{configFeedback.text}</div>}
        </div>
      )}

      {bajoCabecera}

      {error && (
        <div className="error-alert">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      <div
        ref={layoutRef}
        className={`ap-layout ${selectedConversation ? 'has-ficha' : ''} ${redimensionando ? 'is-resizing' : ''}`}
        style={
          {
            '--ap-list-col': `${listaPlegada ? PANEL_PLEGADO_PX : anchos.lista}px`,
            '--ap-ficha-col': `${fichaPlegada ? PANEL_PLEGADO_PX : anchos.ficha}px`,
          } as React.CSSProperties
        }
      >
        {!listaPlegada && (
          <div
            className={`ap-resizer ap-resizer-lista ${redimensionando === 'lista' ? 'is-active' : ''}`}
            onPointerDown={(e) => empezarRedimension('lista', e)}
            onDoubleClick={() => restablecerAncho('lista')}
            role="separator"
            aria-orientation="vertical"
            title="Arrastra para cambiar el ancho (doble clic: ancho por defecto)"
          />
        )}
        {selectedConversation && !fichaPlegada && (
          <div
            className={`ap-resizer ap-resizer-ficha ${redimensionando === 'ficha' ? 'is-active' : ''}`}
            onPointerDown={(e) => empezarRedimension('ficha', e)}
            onDoubleClick={() => restablecerAncho('ficha')}
            role="separator"
            aria-orientation="vertical"
            title="Arrastra para cambiar el ancho (doble clic: ancho por defecto)"
          />
        )}
        <aside className={`ap-list ${listaPlegada ? 'is-collapsed' : ''}`}>
          {listaPlegada ? (
            <>
              <button
                type="button"
                className="ap-collapse-btn"
                onClick={() => togglePanel('lista')}
                title="Mostrar conversaciones"
                aria-label="Mostrar conversaciones"
              >
                <PanelLeftOpen size={18} />
              </button>
              {totalUnread > 0 && <span className="ap-unread-badge">{totalUnread}</span>}
              <span className="ap-collapsed-label">Conversaciones</span>
            </>
          ) : (
            <>
          <div className="ap-panel-head">
            <input
              className="ap-search"
              type="search"
              placeholder="Buscar por nombre, correo o teléfono"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              type="button"
              className="ap-collapse-btn"
              onClick={() => togglePanel('lista')}
              title="Plegar conversaciones"
              aria-label="Plegar conversaciones"
            >
              <PanelLeftClose size={18} />
            </button>
          </div>
          {!loading && filteredConversations.length === 0 && <p className="ap-muted ap-empty">No hay conversaciones abiertas: nadie ha escrito en las últimas 24 h.</p>}
          {filteredConversations.map((conv) => {
            const last = conv.mensajes[conv.mensajes.length - 1];
            const viewers = othersIn(conv.telefono);
            const unread = unreadCount(conv);
            const readInfo = reads[conv.telefono];
            return (
              <button
                type="button"
                key={conv.telefono}
                className={`ap-list-item ${conv.telefono === selectedPhone ? 'is-selected' : ''} ${unread > 0 ? 'is-unread' : ''}`}
                onClick={() => handleSelect(conv.telefono)}
                title={!unread && readInfo ? `Leído por ${readInfo.email === userEmail ? 'ti' : readInfo.nombre}` : undefined}
              >
                <span className="ap-list-top">
                  <span className="ap-list-name">{conv.nombre || conv.correo || `+${conv.telefono}`}</span>
                  {conv.control && (
                    <span className="ap-muted"> · {conv.control.email === userEmail ? 'control: tú' : `control: ${conv.control.nombre}`}</span>
                  )}
                  <span className="ap-list-time">
                    {formatTime(conv.ultimo_mensaje)}
                    {unread > 0 && <span className="ap-unread-badge">{unread}</span>}
                  </span>
                </span>
                <span className="ap-list-bottom">
                  <span className="ap-list-preview">
                    {last ? `${last.entrante ? '' : `${last.autor || 'Asistente'}: `}${last.cuerpo}` : ''}
                  </span>
                  {viewers.length > 0 && (
                    <span className="ap-list-viewers">
                      {viewers.map((person) => (
                        <Avatar key={person.email} person={person} size={20} />
                      ))}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
            </>
          )}
        </aside>

        <div
          className={`ap-chat ${arrastrando ? 'is-dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {arrastrando && (
            <div className="ap-drop-overlay">
              <Paperclip size={22} /> Suelta el PDF para adjuntarlo
            </div>
          )}
          {!selectedConversation ? (
            <div className="ap-chat-empty ap-muted">Selecciona una conversación</div>
          ) : (
            <>
              <div className="ap-chat-head">
                <div>
                  <strong>{selectedConversation.nombre || selectedConversation.correo || `+${selectedConversation.telefono}`}</strong>
                  {reads[selectedConversation.telefono] && reads[selectedConversation.telefono].email !== userEmail && (
                    <span className="ap-muted"> · leído por {reads[selectedConversation.telefono].nombre}</span>
                  )}
                </div>
                {othersHere.length > 0 && (
                  <span className="ap-list-viewers">
                    {othersHere.map((person) => (
                      <Avatar key={person.email} person={person} />
                    ))}
                  </span>
                )}
              </div>

              {selectedConversation.control?.email === userEmail ? (
                <div className="ap-feedback ap-feedback-warning" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span>Tienes el control: el asistente no contesta en esta conversación.</span>
                  <button type="button" className="ap-btn ap-btn-secondary" onClick={() => devolverControl(selectedConversation.telefono)}>
                    Devolver al asistente
                  </button>
                </div>
              ) : selectedConversation.control ? (
                <div className="ap-feedback ap-feedback-warning" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span>Conversación tomada por {selectedConversation.control.nombre}: el asistente no contesta.</span>
                  <button type="button" className="ap-btn" onClick={() => tomarControl(selectedConversation.telefono)}>
                    Tomar control
                  </button>
                </div>
              ) : (
                <div className="ap-feedback" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span>El asistente está contestando en esta conversación.</span>
                  <button type="button" className="ap-btn" onClick={() => tomarControl(selectedConversation.telefono)}>
                    Tomar control
                  </button>
                </div>
              )}

              {othersHere.length > 0 && (
                <div className="ap-warning">
                  <Users size={16} />
                  {othersHere.map((person) => person.nombre).join(', ')}{' '}
                  {othersHere.length === 1 ? 'también está' : 'también están'} en esta conversación
                  {othersTyping.length > 0 && ` · ${othersTyping.map((person) => person.nombre).join(', ')} escribiendo…`}
                </div>
              )}

              <div className="ap-messages">
                {selectedConversation.mensajes.map((msg) => {
                  const isNew = msg.entrante && toMs(msg.fecha) > (highlightFrom[selectedConversation.telefono] ?? Infinity);
                  const firstNew = isNew && msg.sid === firstNewSid;
                  return (
                    <React.Fragment key={msg.sid}>
                      {firstNew && <div className="ap-new-separator">Mensajes nuevos</div>}
                      <div
                        className={`ap-msg ${msg.entrante ? 'ap-msg-in' : msg.humano ? 'ap-msg-human' : 'ap-msg-bot'} ${isNew ? 'ap-msg-new' : ''}`}
                      >
                        {!msg.entrante && <span className="ap-msg-author">{msg.autor || 'Asistente'}</span>}
                        {(esSoloPdf(msg) || (msg.cuerpo !== CUERPO_SOLO_ADJUNTO && msg.cuerpo !== CUERPO_SOLO_UBICACION)) && (
                          <span className="ap-msg-body">
                            {esSoloPdf(msg)
                              ? msg.adjuntos.map((adjunto) => (
                                  <button
                                    key={adjunto.sid}
                                    type="button"
                                    className="ap-msg-pdf"
                                    title="Descargar PDF"
                                    onClick={() => abrirAdjunto(msg.sid, adjunto, 'descargar')}
                                  >
                                    <img src={pdfIcon} alt="PDF adjunto" />
                                  </button>
                                ))
                              : msg.cuerpo}
                          </span>
                        )}
                        {msg.adjuntos?.filter(esImagen).map((adjunto) => (
                          <button
                            key={adjunto.sid}
                            type="button"
                            className="ap-msg-img"
                            title="Abrir imagen"
                            onClick={() => abrirAdjunto(msg.sid, adjunto, 'abrir')}
                          >
                            {imagenes[adjunto.sid] ? (
                              <img src={imagenes[adjunto.sid] as string} alt="Imagen enviada por el usuario" />
                            ) : (
                              <span className="ap-msg-img-loading">
                                <RefreshCw size={16} className="spinner-icon" />
                              </span>
                            )}
                          </button>
                        ))}
                        {msg.ubicacion && (
                          <a className="ap-msg-ubicacion" href={urlMapa(msg.ubicacion)} target="_blank" rel="noopener noreferrer">
                            <iframe
                              title={`Ubicación ${msg.sid}`}
                              src={urlMapaEmbebido(msg.ubicacion)}
                              loading="lazy"
                              tabIndex={-1}
                            />
                            <span className="ap-msg-ubicacion-texto">
                              <MapPin size={14} />
                              {msg.ubicacion.etiqueta || msg.ubicacion.direccion || `${msg.ubicacion.latitud}, ${msg.ubicacion.longitud}`}
                            </span>
                            {msg.ubicacion.etiqueta && msg.ubicacion.direccion && (
                              <span className="ap-muted">{msg.ubicacion.direccion}</span>
                            )}
                          </a>
                        )}
                        {msg.adjuntos?.filter((adjunto) => !esImagen(adjunto)).map((adjunto) => (
                          <span key={adjunto.sid} className="ap-msg-adjunto">
                            <button type="button" className="ap-link-btn" onClick={() => abrirAdjunto(msg.sid, adjunto, 'abrir')}>
                              <Paperclip size={14} /> Abrir archivo
                            </button>
                            <button type="button" className="ap-link-btn" onClick={() => abrirAdjunto(msg.sid, adjunto, 'descargar')}>
                              <Download size={14} /> Descargar
                            </button>
                          </span>
                        ))}
                        <span className="ap-msg-time">
                          {formatTime(msg.fecha)}
                          {!msg.entrante && msg.estado && ` · ${msg.estado}`}
                        </span>
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={chatEndRef} />
              </div>

              {canReply(selectedConversation) && documentoPendiente && (
                <div className="ap-composer-adjunto">
                  <div className="editor-assistant-attachment-chip">
                    <span className="editor-assistant-attachment-chip-icon">
                      <FileText size={14} />
                    </span>
                    <span className="editor-assistant-attachment-chip-name" title={documentoPendiente.name}>
                      {documentoPendiente.name}
                    </span>
                    <button
                      type="button"
                      className="editor-assistant-attachment-chip-remove"
                      onClick={quitarDocumento}
                      disabled={sending}
                      aria-label="Quitar PDF"
                      title="Quitar PDF"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              )}
              {canReply(selectedConversation) ? (
                <div className="ap-composer">
                  <textarea
                    rows={2}
                    placeholder="Escribe la respuesta (Enter para enviar, Shift+Enter para salto de línea)"
                    value={drafts[selectedConversation.telefono] || ''}
                    onChange={(e) => handleDraftChange(e.target.value)}
                    onKeyDown={(e) => {
                      // Enter envía; Shift+Enter añade un salto de línea (no se envía mientras se compone un carácter con IME)
                      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    disabled={sending}
                  />
                  <input
                    ref={documentoInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    hidden
                    onChange={(e) => elegirDocumento(e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    className="editor-assistant-attach-btn"
                    onClick={() => documentoInputRef.current?.click()}
                    disabled={sending}
                    aria-label="Adjuntar PDF"
                    title="Adjuntar PDF (también puedes arrastrarlo al chat)"
                  >
                    <Paperclip size={16} />
                  </button>
                  <button
                    type="button"
                    className="ap-btn"
                    onClick={handleSend}
                    disabled={sending || (!(drafts[selectedConversation.telefono] || '').trim() && !documentoPendiente)}
                  >
                    {sending ? <RefreshCw size={16} className="spinner-icon" /> : <Send size={16} />} Enviar
                  </button>
                </div>
              ) : (
                <div className="ap-feedback ap-feedback-error">
                  Han pasado más de 24 h desde el último mensaje del usuario: WhatsApp no permite enviarle texto libre hasta que vuelva a escribir.
                </div>
              )}
              {sendError && <div className="ap-feedback ap-feedback-error">{sendError}</div>}
            </>
          )}
        </div>

        {selectedConversation && (
          <FichaUsuario conversation={selectedConversation} plegada={fichaPlegada} onToggle={() => togglePanel('ficha')} />
        )}
      </div>
    </section>
  );
};

export default WhatsappChatPanel;
